import { Container, Graphics } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W, BORDER } from '../app/theme';
import { viewport } from '../app/viewport';
import { Button, makeText, avatarSprite, iconSprite, sceneBackground, glassCard } from '../ui/widgets';
import { scaleIn, fadeTo } from '../app/anim';
import type { AppContext } from '../app/AppContext';
import { GameName, RoomName } from '../net/enums';
import { encodeReady, encodeNotReady, type GameInfoPayload } from '../net/codec/matchmaking';
import { gameNameLabel, gameTimeLabel, moneyFa, roundsLabel } from '../util/persian';
import { announceStatus } from '../ui/accessibility';

const READY_GREEN = 0x2ecc71;

/**
 * The ready/confirm screen before a table starts — Unity's "انتظار برای تایید" panel (port of the
 * Network.cs IsReady/WaitReady handling). Two avatar pillars with name pills, the bet/mode/rounds
 * info, and قبول (green) / لغو (red). Creator (IsReady) confirms; joiner (WaitReady) waits.
 * On GameInfo(58) the app navigates to the table; on cancel the server sends ChangeRoom(MainMenu).
 */
export class ReadyScene extends Scene {
  constructor(
    private readonly ctx: AppContext,
    private readonly payload: GameInfoPayload,
    private readonly isCreator: boolean,
  ) {
    super();
  }

  onEnter(): void {
    this.build();
    announceStatus(this.isCreator ? 'حریف پیدا شد. برای شروع بازی تایید کنید.' : 'منتظر تایید حریف باشید.');
  }

  /** Re-flow on viewport-height change: re-pin actions to the live bottom, re-center the info card. */
  onResize(): void {
    this.build();
  }

  private build(): void {
    for (const ch of this.removeChildren()) ch.destroy({ children: true });
    this.addChild(sceneBackground());
    const g = this.payload.game;

    const title = makeText(this.isCreator ? 'حریف پیدا شد' : 'انتظار برای تایید', {
      size: 42,
      weight: '700',
      align: 'center',
      fill: COLORS.gold,
    });
    title.anchor.set(0.5, 0);
    title.position.set(DESIGN_W / 2, 64);
    title.style.dropShadow = { color: 0x2a1c06, alpha: 0.6, blur: 0, distance: 2, angle: Math.PI / 2 };
    this.addChild(title);

    // Game header: which game is starting (icon + name), centered just under the title.
    this.addChild(this.gameHeader(g.gameName, 118));

    // Players glass card: both avatars + a gold «VS» badge + plain usernames (no pills, no win line).
    const cardX = 24;
    const cardW = DESIGN_W - 48;
    const pad = 24;
    const avSize = 130;
    const cardY = 190;
    const cardH = pad + avSize + 14 + 34 + pad;
    this.addChild(glassCard(cardX, cardY, cardW, cardH));
    const avY = cardY + pad;
    const leftX = cardX + 56;
    const rightX = cardX + cardW - 56 - avSize;
    this.addChild(this.avatarBlock(this.payload.player1.avatar, this.payload.player1.username, leftX, avY, avSize));
    this.addChild(this.avatarBlock(this.payload.player2.avatar, this.payload.player2.username, rightX, avY, avSize));
    this.addChild(this.vsBadge(DESIGN_W / 2, avY + avSize / 2));

    // Match-info card: bet · time · rounds, each with a glossy gold icon.
    const card = this.infoCard([
      { icon: 'coin', label: 'شرط', value: moneyFa(g.gamePrice) },
      { icon: 'clock', label: 'زمان', value: gameTimeLabel(g.gameTime) },
      { icon: 'cards', label: 'تعداد دست', value: roundsLabel(g.gameCount) },
    ]);
    card.alpha = 0;
    this.addChild(card);
    fadeTo(card, 1, 300, 160);

    const actionY = viewport.h - 112;
    const infoBottom = this.infoCardY() + 242;
    const noteY = Math.round(infoBottom + Math.max(30, (actionY - infoBottom - 96) / 2));
    this.addChild(this.instructionCard(noteY));

    // Actions.
    if (this.isCreator) {
      const w = (DESIGN_W - 120 - 24) / 2;
      const accept = new Button('تایید و شروع', () => this.ctx.client.send(encodeReady()), {
        width: w,
        height: 80,
        fontSize: 30,
        fill: READY_GREEN,
        icon: this.ctx.iconTexture('save') ?? null,
      });
      accept.position.set(60 + w + 24, actionY);
      this.addChild(accept);
      // Creator cancel: the server zeroes nobody on the P1 path, so it correctly sends ChangeRoom to
      // both players — no local navigate needed (and adding one would double-nav + replay the lobby anim).
      const cancel = new Button('لغو', () => this.ctx.client.send(encodeNotReady(g.uniqueId)), {
        width: w,
        height: 80,
        fontSize: 30,
        fill: COLORS.danger,
      });
      cancel.position.set(60, actionY);
      this.addChild(cancel);
    } else {
      const waiting = makeText('منتظر تایید حریف…', { size: 26, align: 'center', fill: COLORS.gold });
      waiting.anchor.set(0.5);
      waiting.position.set(DESIGN_W / 2, actionY - 48);
      this.addChild(waiting);
      const cancel = new Button('لغو', () => this.cancel(g.uniqueId), {
        width: DESIGN_W - 120,
        height: 72,
        fontSize: 28,
        fill: COLORS.danger,
      });
      cancel.position.set(60, actionY);
      this.addChild(cancel);
    }
  }

  /**
   * Joiner cancel: decline the ready prompt AND locally return to the lobby. Mirrors the Unity cancel
   * button, which both sends NotReady and locally hides the ready panel. The server's NotReady handler
   * zeroes the joiner's id (`waitGame.P2 = 0`) *before* replying, so it sends the joiner's ChangeRoom to
   * player 0 — the joiner never gets it. Navigating locally is safe: the server has already removed this
   * player from the wait game (P2 reset, game back to Created).
   */
  private cancel(uniqueId: string): void {
    this.ctx.client.send(encodeNotReady(uniqueId));
    this.ctx.navigate(RoomName.MainMenu);
  }

  /** The "which game" lockup — game icon + name, centered as one group under the title (RTL: icon right). */
  private gameHeader(game: GameName, y: number): Container {
    const c = new Container();
    const iconSize = 52;
    const gap = 12;
    const name = makeText(gameNameLabel(game), { size: 30, weight: '700', align: 'center', fill: COLORS.gold });
    name.anchor.set(0.5, 0.5);
    const totalW = name.width + gap + iconSize;
    const startX = DESIGN_W / 2 - totalW / 2;
    name.position.set(startX + name.width / 2, y + iconSize / 2);
    const icon = iconSprite(this.ctx.gameIcon(game), iconSize);
    icon.position.set(startX + name.width + gap, y);
    c.addChild(name, icon);
    return c;
  }

  /** An avatar with the player's name as plain bold text beneath it (inside the players card). */
  private avatarBlock(avatar: number, name: string, x: number, y: number, size: number): Container {
    const c = new Container();
    const holder = avatarSprite(this.ctx.profileAtlas, avatar, size);
    holder.position.set(x, y);
    c.addChild(holder);
    scaleIn(holder, 280, 0.7);

    const label = makeText(name, { size: 28, weight: '700', align: 'center', fill: COLORS.white });
    label.anchor.set(0.5, 0);
    label.position.set(x + size / 2, y + size + 14);
    c.addChild(label);
    return c;
  }

  /** A gold «VS» badge for the centre of the two avatar pillars. */
  private vsBadge(cx: number, cy: number): Container {
    const c = new Container();
    c.position.set(cx, cy);
    const r = 34;
    const disc = new Graphics()
      .circle(2, 4, r)
      .fill({ color: COLORS.scrim, alpha: 0.3 }) // soft drop shadow
      .circle(0, 0, r)
      .fill({ color: COLORS.accent })
      .circle(0, 0, r)
      .stroke({ width: BORDER.thick, color: COLORS.accentDark }); // carved bevel ring
    c.addChild(disc);
    const t = makeText('VS', { size: 26, weight: '700', align: 'center', fill: COLORS.buttonTextDark });
    t.anchor.set(0.5);
    c.addChild(t);
    scaleIn(c, 320, 0.5);
    return c;
  }

  /** A carved-wood card listing the match info — each row is [label … value icon] (RTL-friendly). */
  private infoCard(rows: Array<{ icon: string; label: string; value: string }>): Container {
    const c = new Container();
    const cardW = DESIGN_W - 120;
    const pad = 22;
    const rowH = 66;
    const cardH = pad * 2 + rows.length * rowH;
    const cardX = (DESIGN_W - cardW) / 2;
    const cardY = this.infoCardY();

    c.addChild(glassCard(cardX, cardY, cardW, cardH));

    const iconSize = 40;
    rows.forEach((row, i) => {
      const ry = cardY + pad + i * rowH + rowH / 2;
      const ic = iconSprite(this.ctx.iconTexture(row.icon), iconSize);
      ic.position.set(cardX + cardW - pad - iconSize, ry - iconSize / 2);
      c.addChild(ic);

      const value = makeText(row.value, { size: 30, weight: '700', align: 'right', fill: COLORS.gold });
      value.anchor.set(1, 0.5);
      value.position.set(cardX + cardW - pad - iconSize - 16, ry);
      c.addChild(value);

      const label = makeText(row.label, { size: 26, fill: COLORS.text });
      label.anchor.set(0, 0.5);
      label.position.set(cardX + pad + 6, ry);
      c.addChild(label);

      if (i < rows.length - 1) {
        const sep = new Graphics()
          .rect(cardX + pad, cardY + pad + (i + 1) * rowH, cardW - pad * 2, 1)
          .fill({ color: COLORS.panelBorder, alpha: 0.22 });
        c.addChild(sep);
      }
    });
    return c;
  }

  private infoCardY(): number {
    return 500 + Math.round(Math.max(0, viewport.h - 1280) * 0.12);
  }

  /** A calm midpoint status card fills the flexible phone-height region without stretching content. */
  private instructionCard(y: number): Container {
    const c = new Container();
    const x = 80;
    const w = DESIGN_W - 160;
    c.addChild(glassCard(x, y, w, 96));
    const icon = iconSprite(this.ctx.iconTexture(this.isCreator ? 'play' : 'clock'), 48);
    icon.position.set(x + w - 72, y + 24);
    c.addChild(icon);
    const text = makeText(
      this.isCreator ? 'با تایید شما، بازی شروع می‌شود' : 'به محض تایید حریف خبرت می‌کنیم',
      { size: 26, weight: '700', fill: COLORS.text, align: 'center' },
    );
    text.anchor.set(0.5);
    text.position.set(x + w / 2 - 24, y + 48);
    c.addChild(text);
    return c;
  }
}
